<template>
  <div class="whats-new-list">
    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options> Sem resultados </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-button
                v-if="createEnabled"
                variant="primary"
                :to="{ name: 'whats-new-save' }"
              >
                <feather-icon icon="PlusCircleIcon" class="mr-50" />
                <span class="text-nowrap">Novo comunicado</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-table
        id="whats-new-table"
        hover
        class="position-relative"
        :items="items"
        responsive
        :fields="showColumns"
        primary-key="id"
        show-empty
        empty-text="Nenhum comunicado encontrado"
        :busy="loading"
      >
        <template #cell(title)="data">
          <div class="text-wrap">
            <span class="align-text-top font-weight-bold">{{ data.item.title }}</span>
          </div>
        </template>
        <template #cell(version)="data">
          <span>{{ data.item.version || "—" }}</span>
        </template>
        <template #cell(items_count)="data">
          <b-badge variant="light-primary">{{ data.item.items_count || 0 }}</b-badge>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="`light-${statusVariant(data.item.status)}`">
            {{ statusLabel(data.item.status) }}
          </b-badge>
        </template>
        <template #cell(published_at)="data">
          <span>{{ bringMeTheDate(data.item.published_at) || "—" }}</span>
        </template>
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret right boundary="window">
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="updateEnabled"
              :to="{ name: 'whats-new-save', params: { id: data.item.id } }"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Editar</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="updateEnabled && data.item.status !== 'published'"
              @click="changeStatus(data.item, 'published')"
            >
              <feather-icon icon="SendIcon" />
              <span class="align-middle ml-50">Publicar</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="updateEnabled && data.item.status === 'published'"
              @click="changeStatus(data.item, 'archived')"
            >
              <feather-icon icon="ArchiveIcon" />
              <span class="align-middle ml-50">Arquivar</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="deleteEnabled"
              @click="deleteItem(data.item)"
            >
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Remover</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de
              {{ totalItems }} itens</span
            >
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import {
  CREATE_ACTION,
  UPDATE_ACTION,
  DELETE_ACTION,
  WHATS_NEW_RESOURCE,
} from "@/constants/resources";
import {
  WHATS_NEW_STATUS_LABELS,
  WHATS_NEW_STATUS_VARIANTS,
} from "@/constants/whats_new";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import useAppConfig from "@core/app-config/useAppConfig";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
  },
  setup() {
    const { skin } = useAppConfig();
    return { toast: useToast(), skin };
  },
  data() {
    return {
      loading: false,
      currentPage: 1,
      itemsPerPage: 1,
    };
  },
  computed: {
    ...mapGetters({
      searchResult: types.WHATS_NEW_SEARCH_RESULT,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    showColumns() {
      return [
        { key: "title", label: "Título", sortable: true },
        { key: "version", label: "Versão" },
        { key: "items_count", label: "Itens" },
        { key: "status", label: "Status" },
        { key: "published_at", label: "Publicação" },
        { key: "actions", label: "Ações" },
      ];
    },
    createEnabled() {
      return this.$can(CREATE_ACTION, WHATS_NEW_RESOURCE);
    },
    updateEnabled() {
      return this.$can(UPDATE_ACTION, WHATS_NEW_RESOURCE);
    },
    deleteEnabled() {
      return this.$can(DELETE_ACTION, WHATS_NEW_RESOURCE);
    },
    items() {
      return this.searchResult?.data || [];
    },
    totalItems() {
      return this.searchResult?.total || 0;
    },
    pageItemsFrom() {
      return this.searchResult?.from || 0;
    },
    pageItemsTo() {
      return this.searchResult?.to || 0;
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    this.search();
  },
  methods: {
    ...mapActions({
      searchReleases: types.SEARCH_RELEASES,
      deleteRelease: types.DELETE_RELEASE,
      setReleaseStatus: types.SET_RELEASE_STATUS,
    }),
    statusLabel(status) {
      return WHATS_NEW_STATUS_LABELS[status] || status;
    },
    statusVariant(status) {
      return WHATS_NEW_STATUS_VARIANTS[status] || "secondary";
    },
    search(currentPage) {
      this.loading = true;
      this.searchReleases({
        limit: this.itemsPerPage,
        currentPage: Number.isInteger(currentPage)
          ? currentPage
          : this.currentPage,
      })
        .catch(() => {
          this.showError(
            "Ocorreu um erro ao buscar os comunicados. Entre em contato com o setor de TI."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeStatus(item, status) {
      const action = status === "published" ? "publicar" : "arquivar";
      this.$swal({
        title: "Tem certeza?",
        text: `Você irá ${action} o comunicado "${item.title}"`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirmar",
        cancelButtonText: "Cancelar",
        background: `${this.skin === "dark" ? "#283046" : ""}`,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
          htmlContainer: `${this.skin === "dark" ? "text-white" : ""}`,
        },
      }).then((result) => {
        if (result.value) {
          this.setReleaseStatus({ id: item.id, status })
            .then(() => {
              this.search();
              this.showSuccess(`Comunicado atualizado com sucesso!`);
            })
            .catch((err) => {
              this.showError(
                err?.response?.data?.message ||
                  "Ocorreu um erro ao atualizar o comunicado. Entre em contato com o setor de TI."
              );
            });
        }
      });
    },
    deleteItem(item) {
      this.$swal({
        title: "Tem certeza?",
        text: `Você removerá o comunicado "${item.title}"`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        background: `${this.skin === "dark" ? "#283046" : ""}`,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
          htmlContainer: `${this.skin === "dark" ? "text-white" : ""}`,
        },
      }).then((result) => {
        if (result.value) {
          this.deleteRelease(item.id)
            .then(() => {
              this.search();
              this.showSuccess(`O comunicado "${item.title}" foi removido!`);
            })
            .catch(() => {
              this.showError(
                "Ocorreu um erro ao remover o comunicado. Entre em contato com o setor de TI."
              );
            });
        }
      });
    },
    bringMeTheDate(value) {
      if (value) {
        const datePart = String(value).split(/[T ]/)[0];
        const dateParts = datePart.split("-");
        if (dateParts.length === 3) {
          return dateParts[2] + "/" + dateParts[1] + "/" + dateParts[0];
        }
      }
      return "";
    },
    showSuccess(text) {
      this.toast({
        component: ToastificationContent,
        props: { title: "Sucesso", text, icon: "CoffeeIcon", variant: "success" },
      });
    },
    showError(text) {
      this.toast({
        component: ToastificationContent,
        props: { title: "Oops!", text, icon: "AlertTriangleIcon", variant: "danger" },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

// Evita que o container responsivo da tabela corte o menu de ações
// (overflow-x auto vira overflow-y auto e clipa o dropdown).
.whats-new-list .table-responsive {
  overflow: visible;
}
</style>
